import { motion } from 'framer-motion';
import { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { METROS } from '../../data/content';
import { Icons } from '../ui/Icons';
import { Reveal, StaggerGroup, staggerItem } from '../ui/Reveal';

export function CoverageBand() {
  const { openLeadForm } = useApp();
  const [zip, setZip] = useState('');
  const [error, setError] = useState('');

  const submit = () => {
    if (!/^\d{5}$/.test(zip)) {
      setError('Enter a 5-digit ZIP code');
      return;
    }
    setError('');
    openLeadForm({ zip });
  };

  return (
    <section id="coverage" className="relative bg-mist py-20 sm:py-28">
      <div className="pointer-events-none absolute -left-24 top-10 h-80 w-80 rounded-full bg-terracotta/8 blur-3xl" />

      <div className="container-rosa relative">
        <Reveal className="mx-auto max-w-2xl text-center">
          <p className="eyebrow">Across the country</p>
          <h2 className="mt-4 font-display text-[2.1rem] font-bold leading-[1.08] tracking-[-0.01em] text-plum sm:text-4xl">
            Specialists near the families who need them.
          </h2>
          <p className="mt-5 font-body text-lg leading-relaxed text-plum/70">
            Rosa matches families with vetted CAPS-certified specialists right in their area, so help is local, prepared
            and ready when you reach out.
          </p>
        </Reveal>

        {/* Metro cloud */}
        <StaggerGroup className="mt-10 flex flex-wrap justify-center gap-2.5 sm:gap-3">
          {METROS.map((city) => (
            <motion.span
              key={city}
              variants={staggerItem}
              className="inline-flex items-center gap-1.5 rounded-full border border-stone bg-white px-4 py-2 font-body text-sm font-semibold text-plum shadow-soft transition-transform duration-200 hover:-translate-y-0.5"
            >
              <Icons.pin width={16} height={16} className="text-terracotta" />
              {city}
            </motion.span>
          ))}
          <motion.span
            variants={staggerItem}
            className="inline-flex items-center rounded-full border border-terracotta/30 bg-terracotta/8 px-4 py-2 font-body text-sm font-semibold text-terracotta"
          >
            + many more
          </motion.span>
        </StaggerGroup>

        {/* Inline ZIP prompt */}
        <Reveal delay={0.1} className="mx-auto mt-12 max-w-xl">
          <div className="rounded-card border border-stone bg-white p-5 shadow-soft sm:p-6">
            <p className="mb-3 flex items-center gap-2 font-body text-sm font-semibold text-plum">
              <Icons.pin width={17} height={17} className="text-terracotta" /> Enter your ZIP to find your specialist.
            </p>
            <div className="flex flex-col gap-2 sm:flex-row">
              <input
                value={zip}
                onChange={(e) => {
                  setZip(e.target.value.replace(/\D/g, '').slice(0, 5));
                  setError('');
                }}
                onKeyDown={(e) => e.key === 'Enter' && submit()}
                inputMode="numeric"
                placeholder="e.g. 43082"
                aria-label="ZIP code"
                className={`w-full flex-1 rounded-2xl border bg-white px-4 py-3.5 font-body text-plum outline-none transition-colors placeholder:text-plum/40 ${
                  error ? 'border-terracotta' : 'border-stone focus:border-terracotta focus:shadow-glow'
                }`}
              />
              <button onClick={submit} className="btn-primary shrink-0 !py-3.5">
                <Icons.search width={18} height={18} /> Find my specialist
              </button>
            </div>
            {error && <p className="mt-1.5 font-body text-xs text-terracotta-deep">{error}</p>}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
